import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styles from './GamePerformanceWidget.module.css';
import type {
	GamePerformanceCalculationResult,
	GamePerformanceStatus,
	GameThumbnail,
} from '../../services/gamePerformanceService';
import { getMostPopularGames } from '../../services/gamePerformanceService';
import { useViewModel } from '../../hooks/useViewModel';
import MoreGamesPopupV2 from './MoreGamesPopupV2';
import { POPULAR_GAMES_QUERY_KEY } from '../../hooks/usePopularGames';
import GamePerformanceDebugPanel from './GamePerformanceDebugPanel';

interface GamePerformanceWidgetProps {
	result: GamePerformanceCalculationResult;
	selectedGames?: string[];
	onGameTitlesChange: (titles: string[]) => void;
}

const S3_ORIGIN = 'https://s3.amazonaws.com';

const PRELOAD_DELAY_MS = 500;

const GamePerformanceWidget: React.FC<GamePerformanceWidgetProps> = ({ result, selectedGames = [], onGameTitlesChange }) => {
	const { data } = useViewModel();
	const gamesWidget = data?.strings.product.configBundles.gamesWidget;
	const [showPopup, setShowPopup] = useState(false);
	const [isPrerendering, setIsPrerendering] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const queryClient = useQueryClient();
	const preconnectLinkRef = useRef<HTMLLinkElement | null>(null);

	useEffect(() => {
		return () => {
			if (preconnectLinkRef.current) {
				document.head.removeChild(preconnectLinkRef.current);
			}
		};
	}, []);

	const handleEnsureS3Preconnect = () => {
		if (document.querySelector(`link[rel="preconnect"][href="${S3_ORIGIN}"]`)) {
			return;
		}

		const link = document.createElement('link');
		link.rel = 'preconnect';
		link.href = S3_ORIGIN;
		document.head.appendChild(link);
		preconnectLinkRef.current = link;
	};

	const games = result.games.slice(0, 12);

	const stringsForStatus = (status: GamePerformanceStatus) => {
		switch (status) {
			case 'RecommendedRequirementsMet': return { header: gamesWidget?.recommendedRequirementsMetHeader, description: gamesWidget?.recommendedRequirementsMet, style: styles.recommended };
			case 'MinimumRequirementsMet': return { header: gamesWidget?.minimumRequirementsMetHeader, description: gamesWidget?.minimumRequirementsMet, style: styles.minimum };
			case 'RequirementsNotMet': return { header: gamesWidget?.requirementsNotMetHeader, description: gamesWidget?.requirementsNotMet, style: styles.notMet };
			case 'NotKnown': return { header: gamesWidget?.notKnownHeader, description: gamesWidget?.notKnown, style: styles.notKnown };
		}
	};

	const handleOpenPopup = async () => {
		if (isLoading) return;
		handleEnsureS3Preconnect();
		setIsLoading(true);

		const minTimer = new Promise<void>((resolve) => setTimeout(resolve, PRELOAD_DELAY_MS));

		try {
			await queryClient.fetchQuery<GameThumbnail[]>({
				queryKey: [POPULAR_GAMES_QUERY_KEY],
				queryFn: getMostPopularGames,
				staleTime: 1000 * 60 * 5,
			});

			setIsPrerendering(true);

			await minTimer;
		} catch {
			await minTimer;
		} finally {
			setIsPrerendering(false);
			setIsLoading(false);
			setShowPopup(true);
		}
	};

	return (
		<div className={styles.widget}>
			<div className={styles.header}>
				<div>
					<h2 className={styles.title}>{gamesWidget?.headerTitle}</h2>
					<p className={styles.subtitle}>{gamesWidget?.headerDescription}</p>
				</div>
				<button
					className={styles.moreButton}
					onClick={handleOpenPopup}
					onMouseEnter={handleEnsureS3Preconnect}
					onFocus={handleEnsureS3Preconnect}
					disabled={isLoading}
					aria-busy={isLoading}
					aria-label={gamesWidget?.moreGames ?? 'Flere spill'}
				>
					{isLoading && <span className={styles.spinner} aria-hidden="true" />}
					{gamesWidget?.moreGames}
				</button>
			</div>

		<MoreGamesPopupV2
			isOpen={showPopup}
			isPrerendering={isPrerendering}
			onClose={() => setShowPopup(false)}
			initialSelected={selectedGames}
			onConfirm={(selectedGames) => {
				setShowPopup(false);
				onGameTitlesChange(selectedGames);
			}}
		/>

			<div className={styles.cards}>
				{games.map((game) => {
					const { header, description, style } = stringsForStatus(game.status);
					return (
						<div key={game.title} className={styles.card}>
							<div className={styles.imageWrapper}>
								{game.coverImage ? (
									<img src={game.coverImage} alt={game.title} className={styles.image} />
								) : (
									<div className={styles.imagePlaceholder} />
								)}
							</div>
							<div className={`${styles.cardContent} ${style}`}>
								<span className={styles.statusLabel}>{header}</span>
								<p className={styles.description}>{description}</p>
							</div>
						</div>
					);
				})}
			</div>

			{result.debugGamePerformanceResult && (
				<GamePerformanceDebugPanel debugResult={result.debugGamePerformanceResult} />
			)}
		</div>
	);
};

export default GamePerformanceWidget;